// FILE: src/pages/CategoriesPage.jsx
//
// Categories page — UI redesign (design language shared with ShiftsPage).
//
//   - Geist / Geist Mono typography, CSS fully scoped under `.ctpg-root`
//   - Sticky page-top (title + Add button) with a search filter bar
//   - Compact table inside a white card; type chips color-coded to match
//     TransactionPage (Receipt=green, Payment=red, Transfer=orange, C2B=blue)
//   - Add / Edit dialogs re-skinned but still MUI Dialog underneath
//   - Feedback moved from an inline Alert to a bottom-center Snackbar
//
// BEHAVIOR PRESERVED from the previous file:
//   - GET /categories?limit=100000000 (bare-array response), client-side
//     search over name/type/description
//   - POST /categories/create, PUT /categories/:id, DELETE /categories/:id
//     with identical payloads and response-field reads
//   - Hard delete straight from the 3-dot menu WITHOUT confirmation (as before)
//   - The unused handleEditSave stays; the Edit dialog's Save button keeps its
//     own inlined save logic (that is the path actually wired, as before)

import React, { useState, useEffect, useCallback } from "react";
import API from "../api";

// Material UI (kept underneath for behavior/accessibility)
import {
  Dialog,
  DialogContent,
  MenuItem,
  CircularProgress,
  Menu,
  Snackbar,
  Alert,
  useMediaQuery,
  useTheme,
} from "@mui/material";

const categoryTypes = ["Receipt", "Payment", "Transfer", "C2B"];

// ----------------------------------------
// CSS — fully scoped under `.ctpg-root`.
// ----------------------------------------
const CATEGORIES_PAGE_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Geist:wght@400;450;500;600;700&family=Geist+Mono:wght@400;500;600&display=swap');

/* Dialog/menu papers reuse .ctpg-root for tokens — never inherit page sizing */
.MuiPaper-root.ctpg-root {
  min-height: 0;
  background: #fff;
}
.ctpg-root {
  --ctpg-primary: #1E90FF;
  --ctpg-primary-soft: #E8F3FF;
  --ctpg-warn: #FFC107;
  --ctpg-warn-soft: #FFF7DC;
  --ctpg-warn-deep: #B86E00;
  --ctpg-success: #2E7D32;
  --ctpg-success-soft: #E8F5E9;
  --ctpg-danger: #C62828;
  --ctpg-danger-soft: #FDECEC;
  --ctpg-orange: #ED6C02;
  --ctpg-orange-soft: #FDF0E5;
  --ctpg-text-1: #111827;
  --ctpg-text-2: #374151;
  --ctpg-text-3: #6B7280;
  --ctpg-text-4: #9CA3AF;
  --ctpg-border-1: #ECECEC;
  --ctpg-border-2: #DDDDDD;
  --ctpg-surface: #FFFFFF;
  --ctpg-surface-sub: #FAFAFA;
  --ctpg-surface-sub-2: #F4F4F5;
  --ctpg-radius-sm: 6px;
  --ctpg-radius-md: 8px;
  --ctpg-radius-lg: 12px;
  --ctpg-shadow-sm: 0 1px 2px rgba(15, 23, 42, 0.04);
  --ctpg-shadow-md: 0 4px 16px rgba(15, 23, 42, 0.06);
  --ctpg-navbar-h: 60px;

  font-family: 'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-feature-settings: 'cv11', 'ss03';
  color: var(--ctpg-text-1);
  background: var(--ctpg-surface-sub);
  min-height: 100vh;
  padding: 0;
  font-size: 13.5px;
  line-height: 1.5;
  letter-spacing: -0.005em;
}
.ctpg-root * { box-sizing: border-box; }

/* ----- Sticky top region (page header + filter bar) ----- */
.ctpg-sticky-top {
  position: sticky;
  top: var(--ctpg-navbar-h);
  z-index: 30;
  background: var(--ctpg-surface-sub);
  border-bottom: 1px solid var(--ctpg-border-1);
}
.ctpg-header {
  padding: 20px 24px 12px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;
}
.ctpg-title-block { min-width: 0; flex: 1 1 320px; }
.ctpg-title {
  font-size: 22px;
  font-weight: 600;
  letter-spacing: -0.02em;
  margin: 0 0 4px;
  color: var(--ctpg-text-1);
}
.ctpg-subtitle {
  display: flex; align-items: center; gap: 10px;
  font-size: 12.5px; color: var(--ctpg-text-3); flex-wrap: wrap;
}
.ctpg-subtitle-count {
  display: inline-flex; align-items: center; gap: 6px;
  background: var(--ctpg-primary-soft);
  color: #0E5EAB;
  padding: 3px 9px; border-radius: 999px;
  font-size: 11px; font-weight: 500;
  font-family: 'Geist Mono', monospace;
  font-variant-numeric: tabular-nums;
  border: 1px solid #CBE4FB;
}
.ctpg-subtitle-dot { width: 4px; height: 4px; border-radius: 50%; background: var(--ctpg-text-4); }

/* ----- Action bar / buttons ----- */
.ctpg-actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.ctpg-btn {
  display: inline-flex; align-items: center; gap: 7px;
  height: 34px; padding: 0 14px;
  font-family: inherit; font-size: 12.5px; font-weight: 500;
  border-radius: var(--ctpg-radius-md);
  border: 1px solid transparent; cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}
.ctpg-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.ctpg-btn-primary { background: var(--ctpg-primary); color: #fff; }
.ctpg-btn-primary:hover:not(:disabled) { background: #1A7DD9; }
.ctpg-btn-ghost {
  background: var(--ctpg-surface); color: var(--ctpg-text-2);
  border-color: var(--ctpg-border-2);
}
.ctpg-btn-ghost:hover:not(:disabled) { background: var(--ctpg-surface-sub-2); }

/* ----- Filter bar ----- */
.ctpg-filterbar {
  display: flex; align-items: flex-end; gap: 10px; flex-wrap: wrap;
  padding: 14px 24px 16px;
  background: var(--ctpg-surface);
  border-top: 1px solid var(--ctpg-border-1);
}
.ctpg-filter-field { display: flex; flex-direction: column; gap: 4px; }
.ctpg-filter-field label {
  font-size: 10.5px; font-weight: 500; text-transform: uppercase;
  letter-spacing: 0.04em; color: var(--ctpg-text-3);
}
.ctpg-input {
  height: 34px; padding: 0 10px;
  background: var(--ctpg-surface);
  border: 1px solid var(--ctpg-border-2);
  border-radius: var(--ctpg-radius-md);
  font-family: inherit; font-size: 13px; color: var(--ctpg-text-1);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  min-width: 140px;
}
.ctpg-input:focus {
  outline: none;
  border-color: var(--ctpg-primary);
  box-shadow: 0 0 0 3px var(--ctpg-primary-soft);
}
.ctpg-input-search {
  background-image: url("data:image/svg+xml,%3Csvg width='14' height='14' viewBox='0 0 16 16' xmlns='http://www.w3.org/2000/svg' fill='none'%3E%3Ccircle cx='7' cy='7' r='4.5' stroke='%236B7280' stroke-width='1.4'/%3E%3Cpath d='M10.5 10.5l3 3' stroke='%236B7280' stroke-width='1.4' stroke-linecap='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: 10px center;
  padding-left: 30px;
  min-width: 240px;
}
.ctpg-filter-meta {
  margin-left: auto;
  font-size: 11.5px; color: var(--ctpg-text-3);
  display: inline-flex; align-items: center; gap: 4px;
  padding-bottom: 8px;
}
.ctpg-filter-meta b {
  font-family: 'Geist Mono', monospace;
  font-variant-numeric: tabular-nums;
  font-weight: 600; color: var(--ctpg-text-2);
}

/* ----- Body region ----- */
.ctpg-body { padding: 18px 24px 32px; }

/* ----- Table card ----- */
.ctpg-card {
  background: var(--ctpg-surface);
  border: 1px solid var(--ctpg-border-1);
  border-radius: var(--ctpg-radius-lg);
  box-shadow: var(--ctpg-shadow-sm);
  overflow: hidden;
}
.ctpg-table-wrap { width: 100%; overflow-x: auto; }
.ctpg-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;
  min-width: 720px;
}
.ctpg-table thead th {
  background: var(--ctpg-surface);
  color: var(--ctpg-text-2);
  font-size: 11.5px; font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 10px;
  border-bottom: 1px solid var(--ctpg-border-2);
  text-align: left;
  white-space: nowrap;
}
.ctpg-table thead th.r { text-align: right; }
.ctpg-table tbody td {
  padding: 11px 10px;
  border-bottom: 1px solid var(--ctpg-border-1);
  vertical-align: middle;
  color: var(--ctpg-text-1);
}
.ctpg-table tbody tr:last-child td { border-bottom: 0; }
.ctpg-table tbody tr { transition: background 0.1s ease; }
.ctpg-table tbody tr:hover { background: var(--ctpg-surface-sub); }
.ctpg-cat-name { font-weight: 500; color: var(--ctpg-text-1); font-size: 13px; }
.ctpg-cat-desc { color: var(--ctpg-text-3); font-size: 12.5px; max-width: 520px; }
.ctpg-cat-desc.is-empty { color: var(--ctpg-text-4); font-style: italic; }

/* Type chips — colors match TransactionPage action colors */
.ctpg-chip {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 2px 8px 2px 6px;
  font-size: 11px; font-weight: 500;
  border-radius: 999px;
  white-space: nowrap;
}
.ctpg-chip-dot { width: 6px; height: 6px; border-radius: 50%; }
.ctpg-chip-receipt {
  background: var(--ctpg-success-soft); color: var(--ctpg-success);
  border: 1px solid #B7DBBA;
}
.ctpg-chip-receipt .ctpg-chip-dot { background: var(--ctpg-success); }
.ctpg-chip-payment {
  background: var(--ctpg-danger-soft); color: var(--ctpg-danger);
  border: 1px solid #F4C7C7;
}
.ctpg-chip-payment .ctpg-chip-dot { background: var(--ctpg-danger); }
.ctpg-chip-transfer {
  background: var(--ctpg-orange-soft); color: var(--ctpg-orange);
  border: 1px solid #F6CFA9;
}
.ctpg-chip-transfer .ctpg-chip-dot { background: var(--ctpg-orange); }
.ctpg-chip-c2b {
  background: var(--ctpg-primary-soft); color: #0E5EAB;
  border: 1px solid #BBDCF9;
}
.ctpg-chip-c2b .ctpg-chip-dot { background: var(--ctpg-primary); }
.ctpg-chip-other {
  background: var(--ctpg-surface-sub-2); color: var(--ctpg-text-2);
  border: 1px solid var(--ctpg-border-2);
}
.ctpg-chip-other .ctpg-chip-dot { background: var(--ctpg-text-3); }

/* Row actions */
.ctpg-row-actions { display: flex; justify-content: flex-end; align-items: center; }
.ctpg-icon-btn {
  width: 28px; height: 28px;
  display: inline-flex; align-items: center; justify-content: center;
  background: transparent;
  border: 1px solid transparent;
  border-radius: var(--ctpg-radius-sm);
  color: var(--ctpg-text-3);
  cursor: pointer;
  transition: all 0.12s ease;
}
.ctpg-icon-btn:hover {
  background: var(--ctpg-surface-sub-2);
  color: var(--ctpg-text-1);
  border-color: var(--ctpg-border-2);
}

/* ----- Empty / Loading states ----- */
.ctpg-state {
  padding: 56px 32px;
  text-align: center;
  color: var(--ctpg-text-3);
}
.ctpg-state-icon {
  margin: 0 auto 12px;
  width: 44px; height: 44px;
  display: flex; align-items: center; justify-content: center;
  border-radius: 50%;
  background: var(--ctpg-surface-sub-2);
  color: var(--ctpg-text-3);
}
.ctpg-state-title { color: var(--ctpg-text-2); font-size: 14px; font-weight: 500; margin-bottom: 4px; }
.ctpg-state-sub { font-size: 12.5px; color: var(--ctpg-text-3); max-width: 360px; margin: 0 auto; }
.ctpg-state-loading {
  display: flex; flex-direction: column; align-items: center; gap: 10px;
  padding: 56px 32px; color: var(--ctpg-text-3); font-size: 12.5px;
}

/* ----- Row-menu skin (portaled MUI Menu paper carries .ctpg-root) ----- */
.ctpg-root.ctpg-menu-paper {
  min-height: 0;
  border-radius: var(--ctpg-radius-md) !important;
  border: 1px solid var(--ctpg-border-1);
  box-shadow: var(--ctpg-shadow-md) !important;
  min-width: 140px;
}
.ctpg-root .ctpg-menu-item {
  font-family: 'Geist', -apple-system, BlinkMacSystemFont, sans-serif !important;
  font-size: 12.5px !important;
  padding: 7px 14px !important;
  display: flex; align-items: center; gap: 8px;
  color: var(--ctpg-text-2);
}
.ctpg-root .ctpg-menu-item:hover { background: var(--ctpg-surface-sub-2) !important; }
.ctpg-root .ctpg-menu-item-danger { color: var(--ctpg-danger) !important; }
.ctpg-root .ctpg-menu-item-danger:hover { background: var(--ctpg-danger-soft) !important; }

/* ----- Modal skinning (MUI Dialog underneath, custom look) ----- */
.ctpg-dlg .MuiPaper-root {
  font-family: 'Geist', -apple-system, BlinkMacSystemFont, sans-serif !important;
  border-radius: var(--ctpg-radius-lg) !important;
  box-shadow: 0 24px 64px rgba(15, 23, 42, 0.18) !important;
  overflow: hidden;
}
.ctpg-dlg-head {
  display: flex; align-items: flex-start; justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid var(--ctpg-border-1);
}
.ctpg-dlg-title { font-size: 16px; font-weight: 600; color: var(--ctpg-text-1); margin: 0; }
.ctpg-dlg-sub { font-size: 12px; color: var(--ctpg-text-3); margin-top: 3px; }
.ctpg-dlg-close {
  background: none; border: none; color: var(--ctpg-text-3);
  width: 28px; height: 28px; border-radius: 6px; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
}
.ctpg-dlg-close:hover { background: var(--ctpg-surface-sub-2); color: var(--ctpg-text-1); }
.ctpg-dlg-body { padding: 18px 20px; }
.ctpg-dlg-foot {
  padding: 14px 20px;
  border-top: 1px solid var(--ctpg-border-1);
  background: var(--ctpg-surface-sub);
  display: flex; align-items: center; justify-content: flex-end;
  gap: 8px;
}

/* Form field bits inside dialogs */
.ctpg-flab {
  display: block; font-size: 11px; font-weight: 500;
  color: var(--ctpg-text-2); letter-spacing: 0.02em;
  text-transform: uppercase; margin-bottom: 4px;
}
.ctpg-flab-req::after { content: ' *'; color: var(--ctpg-primary); font-weight: 700; }
.ctpg-fhint { font-size: 11px; color: var(--ctpg-text-3); margin-top: 3px; }
.ctpg-frow { margin-bottom: 14px; }
.ctpg-finput, .ctpg-fselect {
  width: 100%; height: 34px; padding: 0 10px;
  border: 1px solid var(--ctpg-border-2);
  border-radius: var(--ctpg-radius-md);
  font-family: inherit; font-size: 13px;
  color: var(--ctpg-text-1); background: #fff;
}
.ctpg-finput:focus, .ctpg-fselect:focus {
  outline: none; border-color: var(--ctpg-primary);
  box-shadow: 0 0 0 3px var(--ctpg-primary-soft);
}
.ctpg-ftextarea {
  width: 100%; min-height: 64px; padding: 8px 10px; resize: vertical;
  border: 1px solid var(--ctpg-border-2); border-radius: var(--ctpg-radius-md);
  font-family: inherit; font-size: 13px; color: var(--ctpg-text-1);
  background: #fff;
}
.ctpg-ftextarea:focus {
  outline: none; border-color: var(--ctpg-primary);
  box-shadow: 0 0 0 3px var(--ctpg-primary-soft);
}
`;

// ----------------------------------------
// Small inline icons
// ----------------------------------------
function IconPlus({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden>
      <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}
function IconClose({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden>
      <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}
function IconDots({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor" aria-hidden>
      <circle cx="3" cy="8" r="1.4" />
      <circle cx="8" cy="8" r="1.4" />
      <circle cx="13" cy="8" r="1.4" />
    </svg>
  );
}
function IconTag({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden>
      <path
        d="M2.5 2.5h5l6 6-5 5-6-6v-5z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <circle cx="5.5" cy="5.5" r="1" fill="currentColor" />
    </svg>
  );
}

// ----------------------------------------
// Reusable: category type chip
// ----------------------------------------
const TYPE_CHIP_CLASS = {
  Receipt: "ctpg-chip-receipt",
  Payment: "ctpg-chip-payment",
  Transfer: "ctpg-chip-transfer",
  C2B: "ctpg-chip-c2b",
};

function TypeChip({ type }) {
  const cls = TYPE_CHIP_CLASS[type] || "ctpg-chip-other";
  return (
    <span className={`ctpg-chip ${cls}`}>
      <span className="ctpg-chip-dot" />
      {type}
    </span>
  );
}

function CategoriesPage() {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("success");

  // For searching
  const [search, setSearch] = useState("");

  // "Add category" dialog states
  const [openAdd, setOpenAdd] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  const [newCatType, setNewCatType] = useState("Receipt");
  const [newCatDesc, setNewCatDesc] = useState("");

  // "Edit category" dialog states
  const [openEdit, setOpenEdit] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editCatName, setEditCatName] = useState("");
  const [editCatType, setEditCatType] = useState("Receipt");
  const [editCatDesc, setEditCatDesc] = useState("");

  // For the "3-dot" menu
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedCat, setSelectedCat] = useState(null);

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      const res = await API.get("/categories?limit=100000000");
      setCategories(res.data || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
      setAlertMessage("Failed to load categories");
      setAlertSeverity("error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Filter categories by search
  const filteredCategories = categories.filter((cat) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      cat.name.toLowerCase().includes(q) ||
      cat.type.toLowerCase().includes(q) ||
      (cat.description || "").toLowerCase().includes(q)
    );
  });

  // ADD Category
  const handleAddCategory = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: newCatName,
        type: newCatType,
        description: newCatDesc,
      };
      const res = await API.post("/categories/create", payload);
      setAlertMessage(res.data.message || "Category added successfully!");
      setAlertSeverity("success");

      setOpenAdd(false);
      setNewCatName("");
      setNewCatType("Receipt");
      setNewCatDesc("");

      fetchCategories();
    } catch (error) {
      console.error("Error adding category:", error);
      setAlertMessage(
        error.response?.data?.message || "Failed to add category. Please try again."
      );
      setAlertSeverity("error");
    }
  };

  // Edit Category
  const handleOpenEditDialog = (cat) => {
    setEditId(cat._id);
    setEditCatName(cat.name);
    setEditCatType(cat.type);
    setEditCatDesc(cat.description || "");
    setOpenEdit(true);
  };

  const handleEditSave = async () => {
    if (!editId) return;
    try {
      const payload = {
        name: editCatName,
        type: editCatType,
        description: editCatDesc,
      };
      const res = await API.put(`/categories/${editId}`, payload);
      setAlertMessage(res.data.message || "Category updated successfully!");
      setAlertSeverity("success");

      setOpenEdit(false);
      fetchCategories();
    } catch (error) {
      console.error("Error editing category:", error);
      setAlertMessage(
        error.response?.data?.message || "Failed to edit category. Please try again."
      );
      setAlertSeverity("error");
    }
  };

  // Delete Category
  const handleDeleteCategory = async (id) => {
    try {
      const res = await API.delete(`/categories/${id}`);
      setAlertMessage(res.data.message || "Category deleted successfully!");
      setAlertSeverity("success");
      fetchCategories();
    } catch (error) {
      console.error("Error deleting category:", error);
      setAlertMessage(
        error.response?.data?.message || "Failed to delete category. Please try again."
      );
      setAlertSeverity("error");
    }
  };

  // 3-dot menu logic
  const handleMenuOpen = (event, cat) => {
    setSelectedCat(cat);
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setSelectedCat(null);
    setMenuAnchor(null);
  };

  // When user chooses Edit from the menu
  const handleMenuEdit = () => {
    if (selectedCat) {
      handleOpenEditDialog(selectedCat);
    }
    handleMenuClose();
  };

  // When user chooses Delete from the menu
  const handleMenuDelete = () => {
    if (selectedCat) {
      handleDeleteCategory(selectedCat._id);
    }
    handleMenuClose();
  };

  return (
    <div className="ctpg-root">
      <style>{CATEGORIES_PAGE_CSS}</style>

      {/* ----- Sticky top: header + search bar ----- */}
      <div className="ctpg-sticky-top">
        <div className="ctpg-header">
          <div className="ctpg-title-block">
            <h1 className="ctpg-title">Categories</h1>
            <div className="ctpg-subtitle">
              <span>
                Label your money movements — Receipt, Payment, Transfer and C2B.
              </span>
              {!loading && (
                <>
                  <span className="ctpg-subtitle-dot" />
                  <span className="ctpg-subtitle-count">
                    {categories.length} total
                  </span>
                </>
              )}
            </div>
          </div>
          <div className="ctpg-actions">
            <button
              className="ctpg-btn ctpg-btn-primary"
              onClick={() => setOpenAdd(true)}
            >
              <IconPlus />
              Add Category
            </button>
          </div>
        </div>

        <div className="ctpg-filterbar">
          <div className="ctpg-filter-field">
            <label htmlFor="ctpg-search">Search</label>
            <input
              id="ctpg-search"
              type="text"
              className="ctpg-input ctpg-input-search"
              placeholder="Search categories..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {!loading && search && (
            <div className="ctpg-filter-meta">
              <b>{filteredCategories.length}</b> of <b>{categories.length}</b>{" "}
              match
            </div>
          )}
        </div>
      </div>

      {/* ----- Body ----- */}
      <div className="ctpg-body">
        <div className="ctpg-card">
          {loading ? (
            <div className="ctpg-state-loading">
              <CircularProgress size={26} />
              <span>Loading categories…</span>
            </div>
          ) : filteredCategories.length === 0 ? (
            <div className="ctpg-state">
              <div className="ctpg-state-icon">
                <IconTag />
              </div>
              <div className="ctpg-state-title">No categories found</div>
              <div className="ctpg-state-sub">
                {search
                  ? `Nothing matches "${search}" — try different keywords or clear the search.`
                  : "Create your first category with the Add Category button above."}
              </div>
            </div>
          ) : (
            <div className="ctpg-table-wrap">
              <table className="ctpg-table">
                <thead>
                  <tr>
                    <th style={{ width: "26%" }}>Name</th>
                    <th style={{ width: "14%" }}>Type</th>
                    <th>Description</th>
                    <th className="r" style={{ width: 56 }} aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {filteredCategories.map((cat) => (
                    <tr key={cat._id}>
                      <td>
                        <span className="ctpg-cat-name">{cat.name}</span>
                      </td>
                      <td>
                        <TypeChip type={cat.type} />
                      </td>
                      <td>
                        {cat.description ? (
                          <span className="ctpg-cat-desc">{cat.description}</span>
                        ) : (
                          <span className="ctpg-cat-desc is-empty">
                            No description
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="ctpg-row-actions">
                          <button
                            className="ctpg-icon-btn"
                            aria-label="Category actions"
                            onClick={(e) => handleMenuOpen(e, cat)}
                          >
                            <IconDots />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* 3-dot menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        PaperProps={{ className: "ctpg-root ctpg-menu-paper" }}
      >
        <MenuItem className="ctpg-menu-item" onClick={handleMenuEdit}>
          Edit
        </MenuItem>
        <MenuItem
          className="ctpg-menu-item ctpg-menu-item-danger"
          onClick={handleMenuDelete}
        >
          Delete
        </MenuItem>
      </Menu>

      {/* Add Category Dialog */}
      <Dialog
        open={openAdd}
        onClose={() => setOpenAdd(false)}
        fullWidth
        maxWidth="sm"
        fullScreen={isSmallScreen}
        className="ctpg-dlg"
        PaperProps={{ className: "ctpg-root" }}
      >
        <style>{CATEGORIES_PAGE_CSS}</style>
        <div className="ctpg-dlg-head">
          <div>
            <div className="ctpg-dlg-title">Add New Category</div>
            <div className="ctpg-dlg-sub">
              Name it, pick a type, and it becomes available on transactions.
            </div>
          </div>
          <button
            className="ctpg-dlg-close"
            onClick={() => setOpenAdd(false)}
            aria-label="Close"
          >
            <IconClose />
          </button>
        </div>
        <DialogContent dividers className="ctpg-dlg-body">
          <div className="ctpg-frow">
            <label className="ctpg-flab ctpg-flab-req" htmlFor="ctpg-add-name">
              Category Name
            </label>
            <input
              id="ctpg-add-name"
              type="text"
              className="ctpg-finput"
              placeholder="e.g. Card sales"
              value={newCatName}
              onChange={(e) => setNewCatName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleAddCategory(e);
              }}
            />
          </div>
          <div className="ctpg-frow">
            <label className="ctpg-flab" htmlFor="ctpg-add-type">
              Type
            </label>
            <select
              id="ctpg-add-type"
              className="ctpg-fselect"
              value={newCatType}
              onChange={(e) => setNewCatType(e.target.value)}
            >
              {categoryTypes.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            <div className="ctpg-fhint">
              Receipt = money in · Payment = money out · Transfer = between
              accounts · C2B = cash to bank
            </div>
          </div>
          <div className="ctpg-frow">
            <label className="ctpg-flab" htmlFor="ctpg-add-desc">
              Description (Optional)
            </label>
            <textarea
              id="ctpg-add-desc"
              className="ctpg-ftextarea"
              placeholder="What this category is used for…"
              value={newCatDesc}
              onChange={(e) => setNewCatDesc(e.target.value)}
            />
          </div>
        </DialogContent>
        <div className="ctpg-dlg-foot">
          <button
            className="ctpg-btn ctpg-btn-ghost"
            onClick={() => setOpenAdd(false)}
          >
            Cancel
          </button>
          <button
            className="ctpg-btn ctpg-btn-primary"
            onClick={handleAddCategory}
          >
            Save
          </button>
        </div>
      </Dialog>

      {/* Edit Category Dialog */}
      <Dialog
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        fullWidth
        maxWidth="sm"
        fullScreen={isSmallScreen}
        className="ctpg-dlg"
        PaperProps={{ className: "ctpg-root" }}
      >
        <style>{CATEGORIES_PAGE_CSS}</style>
        <div className="ctpg-dlg-head">
          <div>
            <div className="ctpg-dlg-title">Edit Category</div>
            <div className="ctpg-dlg-sub">
              Changes apply everywhere this category is used.
            </div>
          </div>
          <button
            className="ctpg-dlg-close"
            onClick={() => setOpenEdit(false)}
            aria-label="Close"
          >
            <IconClose />
          </button>
        </div>
        <DialogContent dividers className="ctpg-dlg-body">
          <div className="ctpg-frow">
            <label className="ctpg-flab ctpg-flab-req" htmlFor="ctpg-edit-name">
              Category Name
            </label>
            <input
              id="ctpg-edit-name"
              type="text"
              className="ctpg-finput"
              value={editCatName}
              onChange={(e) => setEditCatName(e.target.value)}
            />
          </div>
          <div className="ctpg-frow">
            <label className="ctpg-flab" htmlFor="ctpg-edit-type">
              Type
            </label>
            <select
              id="ctpg-edit-type"
              className="ctpg-fselect"
              value={editCatType}
              onChange={(e) => setEditCatType(e.target.value)}
            >
              {categoryTypes.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            <div className="ctpg-fhint">
              Receipt = money in · Payment = money out · Transfer = between
              accounts · C2B = cash to bank
            </div>
          </div>
          <div className="ctpg-frow">
            <label className="ctpg-flab" htmlFor="ctpg-edit-desc">
              Description (Optional)
            </label>
            <textarea
              id="ctpg-edit-desc"
              className="ctpg-ftextarea"
              value={editCatDesc}
              onChange={(e) => setEditCatDesc(e.target.value)}
            />
          </div>
        </DialogContent>
        <div className="ctpg-dlg-foot">
          <button
            className="ctpg-btn ctpg-btn-ghost"
            onClick={() => setOpenEdit(false)}
          >
            Cancel
          </button>
          <button
            className="ctpg-btn ctpg-btn-primary"
            onClick={async () => {
              if (!editId) return;
              try {
                const payload = {
                  name: editCatName,
                  type: editCatType,
                  description: editCatDesc,
                };
                const res = await API.put(`/categories/${editId}`, payload);
                setAlertMessage(res.data.message || "Category updated!");
                setAlertSeverity("success");
                setOpenEdit(false);
                fetchCategories();
              } catch (error) {
                console.error("Error editing category:", error);
                setAlertMessage(
                  error.response?.data?.message || "Failed to edit category."
                );
                setAlertSeverity("error");
              }
            }}
          >
            Save Changes
          </button>
        </div>
      </Dialog>

      {/* Feedback snackbar (was an inline Alert — presentation-level change) */}
      <Snackbar
        open={Boolean(alertMessage)}
        autoHideDuration={4000}
        onClose={() => setAlertMessage("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setAlertMessage("")}
          severity={alertSeverity}
          sx={{ width: "100%" }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default CategoriesPage;
